import { Box, Typography, Stack } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import MzDataGrid from "@markaaz/ui/MzDataGrid";
import MzDatePicker from "@markaaz/ui/MzDatePicker";
import MzPagination from "@markaaz/ui/MzPagination";
import format from "date-fns/format";
import MzAlert from "@markaaz/ui/MzAlert";
import MzButton from "@markaaz/ui/MzButton";
import MzTooltip from "@markaaz/ui/MzTooltip";
import Loading from "components/common/Loading";
import { ReloadIcon } from "assets/icons";

const sx = {
  header: {
    color: "darkBlue.c100"
  },
  description: {
    color: "darkBlue.c100",
    width: "640px",
    paddingTop: "10px"
  },
  dataGrid: {
    "& .MuiDataGrid-virtualScroller": {
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
        backgroundColor: (theme) => `${theme.palette.common.white}`,
        borderRadius: "0px 0px 8px 8px"
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: (theme) => `${theme.palette.darkBlue.c20}`,
        borderRadius: "8px"
      }
    },
    "& .MuiDataGrid-cell": {
      paddingLeft: "20px"
    },
    "& .MuiDataGrid-columnHeader": {
      paddingLeft: "20px"
    }
  }
};

const columns = [
  { field: "jobId", headerName: "Job ID", width: 350, sortable: false },
  {
    field: "createdDate",
    headerName: "Local Date & Time",
    width: 200,
    sortable: false,
    valueGetter: function ({ value }) {
      if (value === null || value === undefined) {
        return {
          date: "-",
          time: "-"
        };
      } else {
        const formattedDate = format(new Date(value), "LL/dd/yyyy");
        const formattedTime = format(new Date(value), "hh:mm a");
        return {
          date: formattedDate,
          time: formattedTime
        };
      }
    },
    renderCell: (params) => (
      <div>
        <span>{params.value.date}</span>
        <br />
        <span>{params.value.time}</span>
      </div>
    )
  },
  {
    field: "jobSubmitted",
    headerName: "Job Submitted",
    width: 200,
    sortable: false,
    valueGetter: function ({ value }) {
      return value || "-";
    }
  },
  {
    field: "inputFileName",
    headerName: "Input Filename Processed",
    width: 260,
    sortable: false,
    valueGetter: function ({ value }) {
      return value || "-";
    }
  },
  {
    field: "jobStatus",
    headerName: "Job Status",
    width: 150,
    sortable: false,
    renderCell: ({ row }) => {
      return row.jobStatus === "Failed" ? (
        <MzTooltip title={row.errorReason} placement="top" arrow>
          <Box display="flex" gap="5px" alignItems="center">
            <ErrorIcon color="error" /> <Typography variant="caption1">{row.jobStatus}</Typography>
          </Box>
        </MzTooltip>
      ) : (
        <Typography variant="caption1">{row.jobStatus}</Typography>
      );
    }
  },
  {
    field: "errorCount",
    headerName: "Error Count",
    width: 150,
    sortable: false,
    valueGetter: function ({ value }) {
      return value || "-";
    }
  },
  {
    field: "totalEntityCount",
    headerName: "Total Entity Count",
    width: 180,
    sortable: false,
    valueGetter: function ({ value }) {
      return value || "-";
    }
  },
  {
    field: "matchCount",
    headerName: "Match Count",
    width: 150,
    sortable: false,
    valueGetter: function ({ value }) {
      return value || "-";
    }
  },
  {
    field: "nonMatchCount",
    headerName: "Non-Match Count",
    width: 220,
    sortable: false,
    valueGetter: function ({ value }) {
      return value || "-";
    }
  },
  {
    field: "matchCountS3ReportReference",
    headerName: "Match Output Filename",
    width: 260,
    sortable: false,
    valueGetter: function ({ value }) {
      if (!value) return "-";

      if (value.length > 40) {
        return value.substring(0, 40) + "...";
      } else {
        return value;
      }
    }
  },
  {
    field: "nonMatchCountS3ReportReference",
    headerName: "Non-Match Output Filename",
    width: 260,
    sortable: false,
    valueGetter: function ({ value }) {
      if (!value) return "-";

      if (value.length > 40) {
        return value.substring(0, 40) + "...";
      } else {
        return value;
      }
    }
  },
  {
    field: "errorCountS3ReportReference",
    headerName: "Error Records Output Filename",
    width: 260,
    sortable: false,
    valueGetter: function ({ value }) {
      return value || "-";
    }
  }
];

export default function BatchStatus({
  rows,
  selectedDate,
  showBatchStatusError,
  pagination,
  onAlertClose,
  onFromDateSelect,
  onToDateSelect,
  onHandleRefresh
}) {
  const hidePagination = rows?.rec_count <= 10;
  return (
    <Box>
      <Box sx={{ marginTop: "80px" }}>
        <Typography variant="h6" sx={sx.header}>
          Status Record
        </Typography>
        <Typography variant="body2" sx={sx.description}>
          View the status and details of the batch and test file validation jobs you have submitted.
        </Typography>
      </Box>

      <Box mt={10}>
        <Stack direction="row" gap={4}>
          <MzDatePicker
            sx={sx.datePicker}
            value={selectedDate.fromDate}
            closeOnSelect={true}
            onChange={onFromDateSelect}
            maxDate={selectedDate.toDate}
            disableFuture
            slotProps={{
              textField: { placeholder: "From: all dates" },
              actionBar: {
                actions: ["clear"]
              }
            }}
          />
          <MzDatePicker
            sx={sx.datePicker}
            value={selectedDate.toDate}
            onChange={onToDateSelect}
            minDate={selectedDate.fromDate}
            disableFuture
            slotProps={{
              textField: { placeholder: "To: all dates" },
              actionBar: {
                actions: ["clear"]
              }
            }}
          />
        </Stack>
        {rows?.details?.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "row-reverse", marginTop: "20px" }}>
            <MzButton
              startIcon={<ReloadIcon />}
              onClick={onHandleRefresh}
              sx={{ paddingRight: "0", marginBottom: "5px" }}
            >
              <Typography variant="button2">Refresh Job Status</Typography>
            </MzButton>
          </Box>
        )}
      </Box>
      <Box mt={3}>
        {!rows?.details ? (
          <Loading />
        ) : (
          <>
            {showBatchStatusError ? (
              <MzAlert
                severity="error"
                sx={{ maxWidth: "352px", maxHeight: "72px", alignItems: "center", marginTop: "20px" }}
                onClose={() => {
                  onAlertClose("jobStatus");
                }}
              >
                No jobs found.
              </MzAlert>
            ) : (
              <>
                <MzDataGrid
                  rowHeight={75}
                  autoHeight
                  pageSize={10}
                  sx={sx.dataGrid}
                  variant="3"
                  autoPageSize
                  columns={columns}
                  rows={rows.details}
                  rowCount={rows.rec_count}
                  paginationMode="server"
                  getRowId={(row) => row.id}
                  disableSelectionOnClick
                  disableColumnMenu={true}
                  hideFooterPagination
                />

                {!hidePagination && (
                  <Stack alignItems="center">
                    <MzPagination count={pagination.count} page={pagination.page} onChange={pagination.onChange} />
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
